import { ChildProps } from '@apollo/client/react/hoc';
import React from 'react';
import I18n from 'i18n-js';
import {
  withCreateUserActionMutation,
  CoreUserActionsCreateMutationFn,
  useCreateUserAction,
} from '@reverbdotcom/commons/src/user_actions/create_action_mutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import {
  UserAvailableActions,
  core_apimessages_UserActionName,
} from '@reverbdotcom/commons/src/gql/graphql';
import { RCGuidePopover, useMediaQuery } from '@reverbdotcom/cadence/components';
import {
  trackDismissUserActionEvent,
  isUserActionAvailable,
  AVAILABLE_ACTION_NAME_TO_I18N_PREFIX,
  availableUserActionsQuery,
  USER_ACTION_LOCAL_STORAGE_KEYS,
  isLoggedOutUserActionAvailable,
} from '@reverbdotcom/commons/src/components/user_actions_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { setItem } from '@reverbdotcom/commons/src/local_storage_utils';

interface ExternalProps {
  anchorComponent: JSX.Element;
  parentComponentName: string;
  userActionName: core_apimessages_UserActionName;
  showCallout: boolean;
  experimentName?: string;
  width?: 'default' | 'narrow';
}

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = ChildProps<ExternalProps & MutationProps>;

export function UserActionCallout(props: IProps) {
  const user = useUser();
  const isMobile = useMediaQuery('mobile');
  const [loggedOutActionAvailable, setLoggedOutActionAvailable] = React.useState(isLoggedOutUserActionAvailable(props.userActionName));

  const { data } = useQuery<UserAvailableActions.Query, UserAvailableActions.Variables>(
    availableUserActionsQuery,
    {
      errorPolicy: 'all',
      ssr: true,
      variables: {
        loggedOut: !!user.loggedOut,
      },
    },
  );

  const i18nPrefix = AVAILABLE_ACTION_NAME_TO_I18N_PREFIX[props.userActionName];
  const createUserActionState = useCreateUserAction(
    props.userActionName,
    data?.me,
    {
      listingId: null,
    },
    props.createUserAction,
  );

  function storeLoggedOutView() {
    if (!user.loggedOut) { return; }
    if (USER_ACTION_LOCAL_STORAGE_KEYS[props.userActionName]) {
      setItem(USER_ACTION_LOCAL_STORAGE_KEYS[props.userActionName], Date.now());
      setLoggedOutActionAvailable(false);
    }
  }
  const isActionAvailable = user.loggedOut ?
    loggedOutActionAvailable
    : isUserActionAvailable(props.userActionName, data?.me?.availableActions, isMobile);
  const showUserActionCallout: boolean = props.showCallout && isActionAvailable;
  const expEnabled = props.experimentName ? isExperimentEnabled(user, props.experimentName) : true;

  if (!showUserActionCallout || !expEnabled) { return (props.anchorComponent); }

  return (
    <RCGuidePopover
      title={i18nPrefix ? I18n.t(`${i18nPrefix}.title`) : ''}
      content={i18nPrefix ? I18n.t(`${i18nPrefix}.subtitle`) : ''}
      isOpen
      onDismiss={() => {
        storeLoggedOutView();
        if (!user.loggedOut) {
          createUserActionState.createUserAction();
        }
        trackDismissUserActionEvent(props.parentComponentName, props.userActionName);
      }}
      anchor={props.anchorComponent}
      width={props.width || 'narrow'}
      position="bottom-end"
      preventAutoFocus
    />
  );
}

export default connect<ExternalProps>([
  withCreateUserActionMutation,
])(UserActionCallout);
